import {
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  setDoc,
  updateDoc,
  deleteDoc,
  deleteField,
  query,
  where,
  orderBy,
  onSnapshot,
  Timestamp,
  limit
} from "firebase/firestore";
import { db } from "./firebase";

// Helpers
const coerceNumber = (value, fallback = 0) => {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string') {
    const n = Number(value.replace(',', '.').trim());
    return Number.isFinite(n) ? n : fallback;
  }
  if (value === null || value === undefined) return fallback;
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
};

// ==================== MOVEMENTS ====================
export const movementsService = {
  // Carregar todos os movimentos de uma empresa
  async getByCompany(companyId) {
    try {
      const q = query(
        collection(db, "movements"),
        where("companyId", "==", companyId),
        orderBy("date", "desc")
      );
      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          // Converter Timestamps para strings se necessário
          date: data.date?.toDate?.()?.toISOString().split('T')[0] || data.date,
          // Converter createdAt para string ISO se for Timestamp
          createdAt: data.createdAt?.toDate?.()?.toISOString() || data.createdAt
        };
      });
    } catch (error) {
      console.error("Erro ao carregar movimentos:", error);
      return [];
    }
  },

  // Escutar mudanças em tempo real
  subscribe(companyId, callback) {
    try {
      const q = query(
        collection(db, "movements"),
        where("companyId", "==", companyId),
        orderBy("date", "desc")
      );
      return onSnapshot(q, (snapshot) => {
        const movements = snapshot.docs.map(doc => {
          const data = doc.data();
          return {
            id: doc.id,
            ...data,
            date: data.date?.toDate?.()?.toISOString().split('T')[0] || data.date,
            // Converter createdAt para string ISO se for Timestamp
            createdAt: data.createdAt?.toDate?.()?.toISOString() || data.createdAt
          };
        });
        callback(movements);
      }, (error) => {
        console.error("Erro na subscrição de movimentos:", error);
        callback([]);
      });
    } catch (error) {
      console.error("Erro ao configurar subscrição de movimentos:", error);
      return () => {}; // Retorna função vazia para cleanup
    }
  },

  // Adicionar movimento
  async add(movement) {
    try {
      // Limpar campos undefined e remover id
      const cleanData = Object.fromEntries(
        Object.entries(movement).filter(([_, v]) => v !== undefined)
      );
      delete cleanData.id; // Garantir que não há id
      
      // Criar timestamp no momento exato em que o movimento é registado
      // Se já existe um createdAt no movimento (ex: ao editar), manter; senão usar agora
      let createdAtTimestamp;
      if (cleanData.createdAt) {
        // Se já tem createdAt, converter se necessário
        if (cleanData.createdAt instanceof Date) {
          createdAtTimestamp = Timestamp.fromDate(cleanData.createdAt);
        } else if (typeof cleanData.createdAt === 'string') {
          createdAtTimestamp = Timestamp.fromDate(new Date(cleanData.createdAt));
        } else {
          createdAtTimestamp = cleanData.createdAt; // Já é Timestamp
        }
      } else {
        // Criar timestamp no momento exato da criação do movimento
        createdAtTimestamp = Timestamp.now();
      }
      
      const docRef = await addDoc(collection(db, "movements"), {
        ...cleanData,
        createdAt: createdAtTimestamp,
        updatedAt: Timestamp.now()
      });
      return docRef.id;
    } catch (error) {
      console.error("Erro ao adicionar movimento:", error);
      throw error;
    }
  },

  // Atualizar movimento
  async update(id, updates) {
    try {
      // Limpar campos undefined antes de atualizar
      let cleanUpdates = Object.fromEntries(
        Object.entries(updates).filter(([_, v]) => v !== undefined)
      );
      // Quando invoiceNumber é string vazia, remover o campo na BD (deleteField)
      // para garantir que "Para Faturar" não fica com número de fatura antigo
      if (cleanUpdates.invoiceNumber === '') {
        cleanUpdates = { ...cleanUpdates, invoiceNumber: deleteField() };
      }
      
      const docRef = doc(db, "movements", id);
      await updateDoc(docRef, {
        ...cleanUpdates,
        updatedAt: Timestamp.now()
      });
    } catch (error) {
      console.error("Erro ao atualizar movimento:", error);
      throw error;
    }
  },

  // Eliminar movimento
  async delete(id) {
    try {
      await deleteDoc(doc(db, "movements", id));
    } catch (error) {
      console.error("Erro ao eliminar movimento:", error);
      throw error;
    }
  }
};

// ==================== CLIENTS ====================
export const clientsService = {
  async getByCompany(companyId) {
    try {
      const q = query(
        collection(db, "clients"),
        where("companyId", "==", companyId)
      );
      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      console.error("Erro ao carregar clientes:", error);
      return [];
    }
  },

  subscribe(companyId, callback) {
    try {
      const q = query(
        collection(db, "clients"),
        where("companyId", "==", companyId)
      );
      return onSnapshot(q, (snapshot) => {
        const clients = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        callback(clients);
      }, (error) => {
        console.error("Erro na subscrição de clientes:", error);
        callback([]);
      });
    } catch (error) {
      console.error("Erro ao configurar subscrição de clientes:", error);
      return () => {};
    }
  },

  async add(client) {
    try {
      // Limpar campos undefined e remover id
      const cleanData = Object.fromEntries(
        Object.entries(client).filter(([_, v]) => v !== undefined)
      );
      delete cleanData.id; // Garantir que não há id
      
      const docRef = await addDoc(collection(db, "clients"), {
        ...cleanData,
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now()
      });
      return docRef.id;
    } catch (error) {
      console.error("Erro ao adicionar cliente:", error);
      throw error;
    }
  },

  async update(id, updates) {
    try {
      // Limpar campos undefined antes de atualizar
      const cleanUpdates = Object.fromEntries(
        Object.entries(updates).filter(([_, v]) => v !== undefined)
      );
      
      const docRef = doc(db, "clients", id);
      await updateDoc(docRef, {
        ...cleanUpdates,
        updatedAt: Timestamp.now()
      });
    } catch (error) {
      console.error("Erro ao atualizar cliente:", error);
      throw error;
    }
  },

  async delete(id) {
    try {
      await deleteDoc(doc(db, "clients", id));
    } catch (error) {
      console.error("Erro ao eliminar cliente:", error);
      throw error;
    }
  }
};

// ==================== TECH JOBS ====================
export const techJobsService = {
  async getByCompany(companyId) {
    try {
      const q = query(
        collection(db, "techJobs"),
        where("companyId", "==", companyId),
        orderBy("createdAt", "desc")
      );
      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          createdAt: data.createdAt?.toDate?.()?.toISOString() || data.createdAt
        };
      });
    } catch (error) {
      console.error("Erro ao carregar assistências técnicas:", error);
      return [];
    }
  },

  subscribe(companyId, callback) {
    try {
      const q = query(
        collection(db, "techJobs"),
        where("companyId", "==", companyId),
        orderBy("createdAt", "desc")
      );
      return onSnapshot(q, (snapshot) => {
        const techJobs = snapshot.docs.map(doc => {
          const data = doc.data();
          return {
            id: doc.id,
            ...data,
            // Garantir que photos seja sempre um array válido
            photos: Array.isArray(data.photos) ? data.photos : [],
            additionalPhotos: Array.isArray(data.additionalPhotos) ? data.additionalPhotos : [],
            budgets: Array.isArray(data.budgets) ? data.budgets : [],
            createdAt: data.createdAt?.toDate?.()?.toISOString() || data.createdAt
          };
        });
        callback(techJobs);
      }, (error) => {
        console.error("Erro na subscrição de assistências técnicas:", error);
        callback([]);
      });
    } catch (error) {
      console.error("Erro ao configurar subscrição de assistências técnicas:", error);
      return () => {};
    }
  },

  async add(techJob) {
    try {
      // Limpar campos undefined e remover id
      const cleanData = Object.fromEntries(
        Object.entries(techJob).filter(([_, v]) => v !== undefined)
      );
      delete cleanData.id; // Garantir que não há id
      
      const docRef = await addDoc(collection(db, "techJobs"), {
        ...cleanData,
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now()
      });
      return docRef.id;
    } catch (error) {
      console.error("Erro ao adicionar assistência técnica:", error);
      throw error;
    }
  },

  async update(id, updates) {
    try {
      // Limpar campos undefined antes de atualizar
      const cleanUpdates = Object.fromEntries(
        Object.entries(updates).filter(([_, v]) => v !== undefined)
      );
      
      const docRef = doc(db, "techJobs", id);
      await updateDoc(docRef, {
        ...cleanUpdates,
        updatedAt: Timestamp.now()
      });
    } catch (error) {
      console.error("Erro ao atualizar assistência técnica:", error);
      throw error;
    }
  },

  async delete(id) {
    try {
      await deleteDoc(doc(db, "techJobs", id));
    } catch (error) {
      console.error("Erro ao eliminar assistência técnica:", error);
      throw error;
    }
  }
};

// Normalizar data de tarefa para yyyy-MM-dd (Timestamp, string ISO, dd/MM/yyyy)
function normalizeTaskDate(value) {
  if (value == null || value === '') return null;
  if (typeof value?.toDate === 'function') {
    return value.toDate().toISOString().slice(0, 10);
  }
  if (typeof value === 'string') {
    if (value.length >= 10 && value[4] === '-' && value[7] === '-') return value.slice(0, 10);
    const parts = value.split(/[/-]/).map(p => (p || '').trim());
    if (parts.length === 3) {
      const [p0, p1, p2] = parts;
      if (p2.length === 4) return `${p2}-${p1.padStart(2, '0')}-${p0.padStart(2, '0')}`;
      if (p0.length === 4) return `${p0}-${p1.padStart(2, '0')}-${p2.padStart(2, '0')}`;
    }
  }
  try {
    const d = new Date(value);
    return isNaN(d.getTime()) ? null : d.toISOString().slice(0, 10);
  } catch {
    return null;
  }
}

// ==================== TASKS ====================
export const tasksService = {
  async getByCompany(companyId) {
    try {
      const q = query(
        collection(db, "tasks"),
        where("companyId", "==", companyId),
        orderBy("date", "desc")
      );
      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => {
        const data = doc.data();
        const dateNorm = normalizeTaskDate(data.date);
        const dateStr = dateNorm ?? (data.date?.toDate ? data.date.toDate().toISOString().slice(0, 10) : (typeof data.date === 'string' ? data.date : null));
        return { id: doc.id, ...data, date: dateStr };
      });
    } catch (error) {
      console.error("Erro ao carregar tarefas:", error);
      return [];
    }
  },

  subscribe(companyId, callback) {
    try {
      const q = query(
        collection(db, "tasks"),
        where("companyId", "==", companyId),
        orderBy("date", "desc")
      );
      return onSnapshot(q, (snapshot) => {
        const tasks = snapshot.docs.map(doc => {
          const data = doc.data();
          const dateNorm = normalizeTaskDate(data.date);
          const dateStr = dateNorm ?? (data.date?.toDate ? data.date.toDate().toISOString().slice(0, 10) : (typeof data.date === 'string' ? data.date : null));
          return { id: doc.id, ...data, date: dateStr };
        });
        callback(tasks);
      }, (error) => {
        console.error("Erro na subscrição de tarefas:", error);
        callback([]);
      });
    } catch (error) {
      console.error("Erro ao configurar subscrição de tarefas:", error);
      return () => {};
    }
  },

  async add(task) {
    try {
      // Limpar campos undefined e remover id
      const cleanData = Object.fromEntries(
        Object.entries(task).filter(([_, v]) => v !== undefined)
      );
      delete cleanData.id; // Garantir que não há id
      
      const docRef = await addDoc(collection(db, "tasks"), {
        ...cleanData,
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now()
      });
      return docRef.id;
    } catch (error) {
      console.error("Erro ao adicionar tarefa:", error);
      throw error;
    }
  },

  async update(id, updates) {
    try {
      // Limpar campos undefined antes de atualizar
      const cleanUpdates = Object.fromEntries(
        Object.entries(updates).filter(([_, v]) => v !== undefined)
      );
      
      const docRef = doc(db, "tasks", id);
      await updateDoc(docRef, {
        ...cleanUpdates,
        updatedAt: Timestamp.now()
      });
    } catch (error) {
      console.error("Erro ao atualizar tarefa:", error);
      throw error;
    }
  },

  async delete(id) {
    try {
      await deleteDoc(doc(db, "tasks", id));
    } catch (error) {
      console.error("Erro ao eliminar tarefa:", error);
      throw error;
    }
  }
};

// ==================== BUDGETS ====================
export const budgetsService = {
  async getByCompany(companyId) {
    try {
      const q = query(
        collection(db, "budgets"),
        where("companyId", "==", companyId),
        orderBy("createdAt", "desc")
      );
      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => {
        const data = doc.data();
        const items = Array.isArray(data.items)
          ? data.items.map((item) => ({
              ...item,
              value: coerceNumber(item?.value, 0)
            }))
          : [];
        return {
          id: doc.id,
          ...data,
          items,
          total: coerceNumber(data.total, 0),
          createdAt: data.createdAt?.toDate?.()?.toISOString() || data.createdAt
        };
      });
    } catch (error) {
      console.error("Erro ao carregar orçamentos:", error);
      return [];
    }
  },

  subscribe(companyId, callback) {
    try {
      const q = query(
        collection(db, "budgets"),
        where("companyId", "==", companyId),
        orderBy("createdAt", "desc")
      );
      return onSnapshot(q, (snapshot) => {
        const budgets = snapshot.docs.map(doc => {
          const data = doc.data();
          const items = Array.isArray(data.items)
            ? data.items.map((item) => ({
                ...item,
                value: coerceNumber(item?.value, 0)
              }))
            : [];
          return {
            id: doc.id,
            ...data,
            items,
            total: coerceNumber(data.total, 0),
            createdAt: data.createdAt?.toDate?.()?.toISOString() || data.createdAt
          };
        });
        callback(budgets);
      }, (error) => {
        console.error("Erro na subscrição de orçamentos:", error);
        callback([]);
      });
    } catch (error) {
      console.error("Erro ao configurar subscrição de orçamentos:", error);
      return () => {};
    }
  },

  async add(budget) {
    try {
      // Limpar campos undefined e remover id
      const cleanData = Object.fromEntries(
        Object.entries(budget).filter(([_, v]) => v !== undefined)
      );
      delete cleanData.id; // Garantir que não há id
      
      const docRef = await addDoc(collection(db, "budgets"), {
        ...cleanData,
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now()
      });
      return docRef.id;
    } catch (error) {
      console.error("Erro ao adicionar orçamento:", error);
      throw error;
    }
  },

  async update(id, updates) {
    try {
      // Limpar campos undefined antes de atualizar
      const cleanUpdates = Object.fromEntries(
        Object.entries(updates).filter(([_, v]) => v !== undefined)
      );
      
      const docRef = doc(db, "budgets", id);
      await updateDoc(docRef, {
        ...cleanUpdates,
        updatedAt: Timestamp.now()
      });
    } catch (error) {
      console.error("Erro ao atualizar orçamento:", error);
      throw error;
    }
  },

  async delete(id) {
    try {
      await deleteDoc(doc(db, "budgets", id));
    } catch (error) {
      console.error("Erro ao eliminar orçamento:", error);
      throw error;
    }
  }
};

// ==================== CASH LOGS ====================
export const cashLogsService = {
  async getByCompany(companyId) {
    try {
      const q = query(
        collection(db, "cashLogs"),
        where("companyId", "==", companyId),
        orderBy("closedAt", "desc")
      );
      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          date: data.date?.toDate?.()?.toISOString().split('T')[0] || data.date,
          closedAt: data.closedAt?.toDate?.()?.toISOString() || data.closedAt
        };
      });
    } catch (error) {
      console.error("Erro ao carregar auditorias de caixa:", error);
      return [];
    }
  },

  subscribe(companyId, callback) {
    try {
      const q = query(
        collection(db, "cashLogs"),
        where("companyId", "==", companyId),
        orderBy("closedAt", "desc")
      );
      return onSnapshot(q, (snapshot) => {
        const cashLogs = snapshot.docs.map(doc => {
          const data = doc.data();
          return {
            id: doc.id,
            ...data,
            date: data.date?.toDate?.()?.toISOString().split('T')[0] || data.date,
            closedAt: data.closedAt?.toDate?.()?.toISOString() || data.closedAt
          };
        });
        callback(cashLogs);
      }, (error) => {
        console.error("Erro na subscrição de auditorias de caixa:", error);
        // Não limpar os logs em caso de erro - apenas logar o erro
        // O erro pode ser temporário (ex: índice em falta)
      });
    } catch (error) {
      console.error("Erro ao configurar subscrição de auditorias de caixa:", error);
      return () => {};
    }
  },

  async add(cashLog) {
    try {
      // Limpar campos undefined e remover id
      const cleanData = Object.fromEntries(
        Object.entries(cashLog).filter(([_, v]) => v !== undefined)
      );
      delete cleanData.id; // Garantir que não há id
      
      // Converter date para Timestamp se for string
      const dateToSave = cleanData.date 
        ? (typeof cleanData.date === 'string' 
            ? Timestamp.fromDate(new Date(cleanData.date)) 
            : cleanData.date)
        : Timestamp.now();
      
      // Converter closedAt para Timestamp se for string
      const closedAtToSave = cleanData.closedAt 
        ? (typeof cleanData.closedAt === 'string' 
            ? Timestamp.fromDate(new Date(cleanData.closedAt)) 
            : cleanData.closedAt)
        : Timestamp.now();
      
      const docRef = await addDoc(collection(db, "cashLogs"), {
        ...cleanData,
        date: dateToSave,
        closedAt: closedAtToSave,
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now()
      });
      return docRef.id;
    } catch (error) {
      console.error("Erro ao adicionar auditoria de caixa:", error);
      throw error;
    }
  },

  async delete(id) {
    try {
      await deleteDoc(doc(db, "cashLogs", id));
    } catch (error) {
      console.error("Erro ao eliminar auditoria de caixa:", error);
      throw error;
    }
  }
};

// ==================== USERS ====================
export const usersService = {
  // Carregar todos os usuários
  async getAll() {
    try {
      const snapshot = await getDocs(collection(db, "users"));
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      console.error("Erro ao carregar utilizadores:", error);
      return [];
    }
  },

  // Escutar mudanças em tempo real
  subscribe(callback) {
    try {
      return onSnapshot(collection(db, "users"), (snapshot) => {
        const users = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        callback(users);
      }, (error) => {
        console.error("Erro na subscrição de utilizadores:", error);
        callback([]);
      });
    } catch (error) {
      console.error("Erro ao configurar subscrição de utilizadores:", error);
      return () => {};
    }
  },

  // Adicionar usuário
  async add(user) {
    try {
      // Limpar campos undefined e remover id
      const cleanData = Object.fromEntries(
        Object.entries(user).filter(([_, v]) => v !== undefined)
      );
      delete cleanData.id; // Garantir que não há id
      
      const docRef = await addDoc(collection(db, "users"), {
        ...cleanData,
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now()
      });
      return docRef.id;
    } catch (error) {
      console.error("Erro ao adicionar utilizador:", error);
      throw error;
    }
  },

  // Atualizar usuário
  async update(id, updates) {
    try {
      // Limpar campos undefined antes de atualizar
      const cleanUpdates = Object.fromEntries(
        Object.entries(updates).filter(([_, v]) => v !== undefined)
      );
      
      const docRef = doc(db, "users", id);
      await updateDoc(docRef, {
        ...cleanUpdates,
        updatedAt: Timestamp.now()
      });
    } catch (error) {
      console.error("Erro ao atualizar utilizador:", error);
      throw error;
    }
  },

  // Eliminar usuário
  async delete(id) {
    try {
      await deleteDoc(doc(db, "users", id));
    } catch (error) {
      console.error("Erro ao eliminar utilizador:", error);
      throw error;
    }
  }
};

// ==================== AC/PANEL SOLAR RECORDS ====================
export const acPanelRecordsService = {
  // =========================================================
  // AC/Painel Solar - "Eliminação permanente"
  //
  // Problema: estes registos podem ser recriados automaticamente por importação
  // (upsert de movimentos/orçamentos). Para respeitar "Eliminar = eliminar",
  // guardamos uma marca (tombstone) que bloqueia re-importações futuras para
  // o mesmo companyId + movementId/budgetId.
  // =========================================================
  _suppressionDocId(companyId, kind, sourceId) {
    return `${companyId}__${kind}__${sourceId}`;
  },

  async _isSuppressed(companyId, kind, sourceId) {
    try {
      if (!companyId || !kind || !sourceId) return false;
      const ref = doc(db, "acPanelSuppressions", this._suppressionDocId(companyId, kind, sourceId));
      const snap = await getDoc(ref);
      return snap.exists();
    } catch (error) {
      // Em caso de erro, NÃO bloquear o fluxo (fallback: permitir).
      console.warn("Aviso ao verificar supressão AC/Painel Solar:", error);
      return false;
    }
  },

  async _suppress(companyId, kind, sourceId) {
    try {
      if (!companyId || !kind || !sourceId) return;
      const ref = doc(db, "acPanelSuppressions", this._suppressionDocId(companyId, kind, sourceId));
      await setDoc(ref, {
        companyId,
        kind,
        sourceId,
        deletedAt: Timestamp.now()
      });
    } catch (error) {
      console.warn("Aviso ao suprimir AC/Painel Solar:", error);
    }
  },

  async getById(id) {
    try {
      if (!id) return null;
      const snap = await getDoc(doc(db, "acPanelRecords", id));
      if (!snap.exists()) return null;
      const data = snap.data();
      return {
        id: snap.id,
        ...data,
        photos: Array.isArray(data.photos) ? data.photos : [],
        date: data.date || data.createdAt?.toDate?.()?.toISOString().split('T')[0] || new Date().toISOString().split('T')[0],
        createdAt: data.createdAt?.toDate?.()?.toISOString() || data.createdAt
      };
    } catch (error) {
      console.error("Erro ao carregar registo AC/Painel Solar por id:", error);
      return null;
    }
  },

  // Eliminação do utilizador: cria "tombstone" para impedir re-importação futura
  async userDelete(id) {
    try {
      if (!id) return;
      const record = await this.getById(id);
      if (!record) return;

      const companyId = record.companyId;
      if (companyId && record.movementId) {
        await this._suppress(companyId, "MOVEMENT", record.movementId);
      }
      if (companyId && record.budgetId) {
        await this._suppress(companyId, "BUDGET", record.budgetId);
      }

      await this.delete(id);
    } catch (error) {
      console.error("Erro ao eliminar permanentemente registo AC/Painel Solar:", error);
      throw error;
    }
  },
  async getByCompany(companyId) {
    try {
      const q = query(
        collection(db, "acPanelRecords"),
        where("companyId", "==", companyId),
        orderBy("date", "desc")
      );
      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          // Garantir que photos seja sempre um array válido
          photos: Array.isArray(data.photos) ? data.photos : [],
          date: data.date || data.createdAt?.toDate?.()?.toISOString().split('T')[0] || new Date().toISOString().split('T')[0],
          createdAt: data.createdAt?.toDate?.()?.toISOString() || data.createdAt
        };
      });
    } catch (error) {
      console.error("Erro ao carregar registos AC/Painel Solar:", error);
      return [];
    }
  },

  subscribe(companyId, callback) {
    let unsubscribe = () => {};
    let usingFallback = false;

    const mapDocs = (snapshot) =>
      snapshot.docs.map((d) => {
        const data = d.data();
        return {
          id: d.id,
          ...data,
          photos: Array.isArray(data.photos) ? data.photos : [],
          date:
            data.date ||
            data.createdAt?.toDate?.()?.toISOString().split('T')[0] ||
            new Date().toISOString().split('T')[0],
          createdAt: data.createdAt?.toDate?.()?.toISOString() || data.createdAt
        };
      });

    const emit = (records, { manualSort = false } = {}) => {
      const out = manualSort
        ? [...records].sort((a, b) => {
            const dateA = new Date(a.date || a.createdAt || 0);
            const dateB = new Date(b.date || b.createdAt || 0);
            return dateB - dateA;
          })
        : records;
      callback(out);
    };

    try {
      const qOrdered = query(
        collection(db, "acPanelRecords"),
        where("companyId", "==", companyId),
        orderBy("date", "desc")
      );

      const qNoOrder = query(
        collection(db, "acPanelRecords"),
        where("companyId", "==", companyId)
      );

      unsubscribe = onSnapshot(
        qOrdered,
        (snapshot) => emit(mapDocs(snapshot)),
        (error) => {
          console.error("❌ Erro na subscrição de registos AC/Painel Solar:", error);

          // Se o erro for de índice, trocar para um snapshot sem orderBy e ordenar localmente.
          if (error?.code === "failed-precondition" && !usingFallback) {
            usingFallback = true;
            console.warn("⚠️ Tentando carregar sem orderBy devido a erro de índice...");
            try {
              // Parar a subscrição atual antes de iniciar a fallback
              unsubscribe?.();
              unsubscribe = onSnapshot(
                qNoOrder,
                (snapshot) => emit(mapDocs(snapshot), { manualSort: true }),
                (err) => {
                  console.error("❌ Erro ao carregar sem orderBy:", err);
                  callback([]);
                }
              );
            } catch (fallbackError) {
              console.error("❌ Erro no fallback:", fallbackError);
              callback([]);
            }
          } else {
            callback([]);
          }
        }
      );

      return () => unsubscribe?.();
    } catch (error) {
      console.error("Erro ao configurar subscrição de registos AC/Painel Solar:", error);
      return () => {};
    }
  },

  async add(record) {
    try {
      // Limpar campos undefined e remover id
      const cleanData = Object.fromEntries(
        Object.entries(record).filter(([_, v]) => v !== undefined)
      );
      delete cleanData.id; // Garantir que não há id
      
      // Garantir que date é sempre uma string no formato yyyy-MM-dd
      let dateToSave = cleanData.date;
      if (!dateToSave) {
        dateToSave = new Date().toISOString().split('T')[0];
      } else if (typeof dateToSave === 'string' && dateToSave.includes('T')) {
        dateToSave = dateToSave.split('T')[0];
      }
      
      const docRef = await addDoc(collection(db, "acPanelRecords"), {
        ...cleanData,
        date: dateToSave, // Sempre guardar como string no formato yyyy-MM-dd
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now()
      });
      return docRef.id;
    } catch (error) {
      console.error("Erro ao adicionar registo AC/Painel Solar:", error);
      throw error;
    }
  },

  async findByMovementId(companyId, movementId) {
    try {
      if (!companyId || !movementId) return null;
      const q = query(
        collection(db, "acPanelRecords"),
        where("companyId", "==", companyId),
        where("movementId", "==", movementId),
        limit(1)
      );
      const snapshot = await getDocs(q);
      const first = snapshot.docs[0];
      if (!first) return null;
      const data = first.data();
      return {
        id: first.id,
        ...data,
        photos: Array.isArray(data.photos) ? data.photos : [],
        date: data.date || data.createdAt?.toDate?.()?.toISOString().split('T')[0] || new Date().toISOString().split('T')[0],
        createdAt: data.createdAt?.toDate?.()?.toISOString() || data.createdAt
      };
    } catch (error) {
      console.error("Erro ao procurar registo AC/Painel Solar por movementId:", error);
      return null;
    }
  },

  async findByBudgetId(companyId, budgetId) {
    try {
      if (!companyId || !budgetId) return null;
      const q = query(
        collection(db, "acPanelRecords"),
        where("companyId", "==", companyId),
        where("budgetId", "==", budgetId),
        limit(1)
      );
      const snapshot = await getDocs(q);
      const first = snapshot.docs[0];
      if (!first) return null;
      const data = first.data();
      return {
        id: first.id,
        ...data,
        photos: Array.isArray(data.photos) ? data.photos : [],
        date: data.date || data.createdAt?.toDate?.()?.toISOString().split('T')[0] || new Date().toISOString().split('T')[0],
        createdAt: data.createdAt?.toDate?.()?.toISOString() || data.createdAt
      };
    } catch (error) {
      console.error("Erro ao procurar registo AC/Painel Solar por budgetId:", error);
      return null;
    }
  },

  async upsertMovementRecord(record) {
    try {
      const companyId = record?.companyId;
      const movementId = record?.movementId;
      if (!companyId || !movementId) {
        throw new Error("upsertMovementRecord requer companyId e movementId");
      }
      // Se foi eliminado pelo utilizador, nunca re-criar automaticamente
      const suppressed = await this._isSuppressed(companyId, "MOVEMENT", movementId);
      if (suppressed) {
        return null;
      }
      const existing = await this.findByMovementId(companyId, movementId);
      if (existing?.id) {
        // Importante: não forçar photos aqui (para não apagar fotos já adicionadas no card)
        const updates = { ...record };
        delete updates.id;
        await this.update(existing.id, updates);
        return existing.id;
      }
      return await this.add(record);
    } catch (error) {
      console.error("Erro no upsertMovementRecord (AC/Painel Solar):", error);
      throw error;
    }
  },

  async upsertBudgetRecord(record) {
    try {
      const companyId = record?.companyId;
      const budgetId = record?.budgetId;
      if (!companyId || !budgetId) {
        throw new Error("upsertBudgetRecord requer companyId e budgetId");
      }
      // Se foi eliminado pelo utilizador, nunca re-criar automaticamente
      const suppressed = await this._isSuppressed(companyId, "BUDGET", budgetId);
      if (suppressed) {
        return null;
      }
      const existing = await this.findByBudgetId(companyId, budgetId);
      if (existing?.id) {
        const updates = { ...record };
        delete updates.id;
        await this.update(existing.id, updates);
        return existing.id;
      }
      return await this.add(record);
    } catch (error) {
      console.error("Erro no upsertBudgetRecord (AC/Painel Solar):", error);
      throw error;
    }
  },

  async update(id, updates) {
    try {
      // Limpar campos undefined antes de atualizar
      const cleanUpdates = Object.fromEntries(
        Object.entries(updates).filter(([_, v]) => v !== undefined)
      );
      
      const docRef = doc(db, "acPanelRecords", id);
      await updateDoc(docRef, {
        ...cleanUpdates,
        updatedAt: Timestamp.now()
      });
    } catch (error) {
      console.error("Erro ao atualizar registo AC/Painel Solar:", error);
      throw error;
    }
  },

  async delete(id) {
    try {
      await deleteDoc(doc(db, "acPanelRecords", id));
    } catch (error) {
      console.error("Erro ao eliminar registo AC/Painel Solar:", error);
      throw error;
    }
  }
};

